import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import RemoveWordDialog from "../../pzl_common/verify_coord/RemoveWordDialog";
import Logger from "../../../services/Logger";
import ColorService from "../../../services/ColorService";
import AdminGridService from "./AdminGridService";

const DRAFT = "DRAFT";
const READY_TO_REVIEW = "READY_TO_REVIEW";
const REVIEWED = "REVIEWED";
const PUBLISHED = "PUBLISHED";

const STATE_ROUTE_MAP = {
    [DRAFT]: "/admin/puzzle/grid_board/draft",
    [READY_TO_REVIEW]: "/admin/puzzle/grid_board/ready-to-review",
    [REVIEWED]: "/admin/puzzle/grid_board/reviewed",
    [PUBLISHED]: "/admin/puzzle/grid_board/published"
};

export default function AdminGridTplVerifyCoord() {
    const navigate = useNavigate();
    const [results, setResults] = useState([]);
    const [loading, setLoading] = useState(true);
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const fetchResults = useCallback(async () => {
        setLoading(true);
        try {
            const result = await AdminGridService.verifyFilledCoord();
            const items = Array.isArray(result) ? result.filter((r) => r && (r.tplId || r.hwTplId)) : [];
            setResults(items);
        } catch (err) {
            Logger.error("Error verifying filled coord:", err);
            setResults([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchResults();
    }, [fetchResults]);

    const handleRowClick = (row) => {
        setSelectedRow(row);
        setRemoveDialogOpen(true);
    };

    const handleOpenInNewWindow = (e, row) => {
        e.stopPropagation();
        const tplId = row.tplId || row.hwTplId;
        const base = STATE_ROUTE_MAP[row.state] || STATE_ROUTE_MAP[DRAFT];
        const url = `${window.location.origin}${base}/${tplId}`;
        window.open(url, "_blank", "noopener,noreferrer");
    };

    const tplId = selectedRow?.tplId || selectedRow?.hwTplId;

    return (
        <Box sx={{ flexGrow: 1, p: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                <Button variant="outlined" onClick={() => navigate("/admin/puzzle/grid_tpl")} size="small">
                    ← Back
                </Button>
                <Typography variant="h6">Verify Coordinates — Grid puzzles with words to remove (no exclusive cells)</Typography>
                <Button variant="outlined" onClick={fetchResults} disabled={loading} size="small">
                    Refresh
                </Button>
            </Box>

            {loading ? (
                <Box sx={{ py: 6, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                    <CircularProgress />
                    <span>Verifying puzzles…</span>
                </Box>
            ) : results.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
                    No puzzles found with words that must be removed.
                </Box>
            ) : (
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell>Title</TableCell>
                            <TableCell>State</TableCell>
                            <TableCell>Language</TableCell>
                            <TableCell>Words to remove</TableCell>
                            <TableCell padding="none" width={48} />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {results.map((row) => {
                            const rowTplId = row.tplId || row.hwTplId;
                            return (
                                <TableRow
                                    key={rowTplId}
                                    hover
                                    onClick={() => handleRowClick(row)}
                                    sx={{ cursor: "pointer" }}
                                >
                                    <TableCell>{row.title || "—"}</TableCell>
                                    <TableCell>
                                        {row.state ? (
                                            <Chip
                                                label={String(row.state).toUpperCase()}
                                                size="small"
                                                sx={{
                                                    backgroundColor: ColorService.getStateColor(row.state),
                                                    color: "white",
                                                    textTransform: "uppercase"
                                                }}
                                            />
                                        ) : (
                                            "—"
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {row.language ? (
                                            <Chip
                                                label={row.language.toLowerCase() === "french" ? "FR" : row.language.toLowerCase() === "english" ? "EN" : (row.language || "").substring(0, 2).toUpperCase()}
                                                size="small"
                                                variant="outlined"
                                                sx={{ fontWeight: "bold", minWidth: 32, height: 24 }}
                                            />
                                        ) : (
                                            "—"
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        {Array.isArray(row.words) && row.words.length > 0
                                            ? row.words.join(", ")
                                            : "—"}
                                    </TableCell>
                                    <TableCell onClick={(e) => e.stopPropagation()}>
                                        <Tooltip title="Open in new tab">
                                            <IconButton size="small" onClick={(e) => handleOpenInNewWindow(e, row)}>
                                                <OpenInNewIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            )}

            <RemoveWordDialog
                open={removeDialogOpen}
                onClose={() => {
                    setRemoveDialogOpen(false);
                    setSelectedRow(null);
                }}
                tplId={tplId}
                wordsToRemove={selectedRow?.words || []}
                onRefresh={fetchResults}
                puzzleType="grid"
            />
        </Box>
    );
}
